import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppLiveRules } from "./AppLiveRules";

// AppLiveTyping - "typing…" ON THE CHAT LIST'S CARDS (2026-09-30, `Bridge/src/TypingState.ts`). The list feed
// (`AppLiveList`) learns from `/live` which 1:1 chats have a contact typing now; this paints that onto the cards
// (`li.card[data-chat]`, written by `AppViews.Card`) WITHOUT redrawing the list: the card gets the class `wac-typing-on`
// and a `span.r.wac-typing` "typing…" beside its preview line, which `app.css` shows in the accent colour in the preview's
// place. ⚠ The list is redrawn by other code (search, a re-read index) - a `MutationObserver` puts the marks back; the
// paint is idempotent, so its own changes settle at once.
// ⛔ Text by `textContent`; no inline style (the CSP); nothing here can send (decision 9).
// ⚠ SHAPED BY INTERFACES so the suite drives it with a fake document.

/** What the list feed hands the typing chats to. */
export interface IAppTypingCards {
    Show(Chats: string[]): void;
}

/** Nothing to paint on (no document - the suite, a Node typecheck). */
export class AppTypingCardsNone implements IAppTypingCards {
    /** DOCUMENTED EXCEPTION: a no-op. ERROR-SUPPRESSED-JUSTIFIED: nothing to do, cannot throw. */
    public Show = (Chats: string[]): void => {
        return;
    };
}

export interface ITypingCardNode {
    textContent: string | null;
    className: string;
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    querySelector(Selector: string): ITypingCardNode | null;
    appendChild(Child: ITypingCardNode): unknown;
    insertBefore(Child: ITypingCardNode, Ref: ITypingCardNode | null): unknown;
    remove(): void;
    classList: { add(Name: string): void; remove(Name: string): void; contains(Name: string): boolean };
}

export interface ITypingCardDoc {
    querySelectorAll(Selector: string): ArrayLike<ITypingCardNode>;
    createElement(Tag: string): ITypingCardNode;
}

export class AppTypingCards implements IAppTypingCards {
    /** The card's mark while its contact types (`app.css` hides the preview line under it). */
    public static readonly OnClass: string = "wac-typing-on";
    /** The cards: every chat card the list draws. */
    public static readonly CardSelector: string = "li.card[data-chat]";

    private Doc: ITypingCardDoc;
    private Chats: string[] = [];

    public constructor(Doc: ITypingCardDoc) {
        this.Doc = Doc;
    }

    /** The page's painter, made once (one observer for the visit, whichever list feed is running). */
    private static Page: IAppTypingCards | null = null;

    /** The page's own document, watched for list redraws - or nothing to paint on outside a browser. */
    public static ForPage = (): IAppTypingCards => {
        if (getCoreUtils().KErrorMode === true) { return new AppTypingCardsNone(); }
        try {
            if (AppTypingCards.Page !== null) { return AppTypingCards.Page as IAppTypingCards; }
            if (typeof document === "undefined" || typeof MutationObserver === "undefined") { return new AppTypingCardsNone(); }
            const Made: AppTypingCards = new AppTypingCards(document as unknown as ITypingCardDoc);
            const Watch: MutationObserver = new MutationObserver(Made.OnMutated);
            Watch.observe(document.body, { childList: true, subtree: true });
            AppTypingCards.Page = Made;
            return Made;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTypingCards.ForPage");
            return new AppTypingCardsNone();
        }
    };

    /** These chats are typing now (all others are not). */
    public Show = (Chats: string[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Chats = Chats.slice(0);
            this.Paint();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTypingCards.Show");
        }
    };

    /** The list was redrawn: put the marks back (only when someone is typing - otherwise nothing to do). */
    public OnMutated = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Chats.length > 0) { this.Paint(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTypingCards.OnMutated");
        }
    };

    /** Each card: marked when its chat types, unmarked when not. ⛔ Idempotent - a card already right is not touched. */
    public Paint = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Cards: ArrayLike<ITypingCardNode> = this.Doc.querySelectorAll(AppTypingCards.CardSelector);
            for (let At: number = 0; At < Cards.length; At = At + 1) {
                const Card: ITypingCardNode = Cards[At];
                const Want: boolean = this.Chats.indexOf(Card.getAttribute("data-chat") || "") >= 0;
                const Line: ITypingCardNode | null = Card.querySelector("." + AppLiveRules.TypingClass);
                if (Want === true && Line === null) { this.Mark(Card); continue; }
                if (Want === false && (Line !== null || Card.classList.contains(AppTypingCards.OnClass) === true)) {
                    Card.classList.remove(AppTypingCards.OnClass);
                    if (Line !== null) { (Line as ITypingCardNode).remove(); }
                }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTypingCards.Paint");
        }
    };

    /** One card: its mark and its "typing…" line, inside the card's link where the preview is (before the meta line). */
    private Mark = (Card: ITypingCardNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            let Home: ITypingCardNode | null = Card.querySelector("a");
            if (Home === null) { Home = Card; }
            const Line: ITypingCardNode = this.Doc.createElement("span");
            Line.className = "r " + AppLiveRules.TypingClass;
            Line.textContent = AppLiveRules.TypingText("composing");
            const Meta: ITypingCardNode | null = (Home as ITypingCardNode).querySelector(".m");
            (Home as ITypingCardNode).insertBefore(Line, Meta);
            Card.classList.add(AppTypingCards.OnClass);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTypingCards.Mark");
        }
    };
}
